import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Simulate user authentication check
    const token = localStorage.getItem('authToken');
    if (token) {
      // Simulate user data
      setUser({
        name: 'John Doe',
        role: 'admin', // 'user', 'agent', 'admin'
        avatar: '/assets/images/avatar.jpg'
      });
    }

    // Simulate notification updates
    const notificationInterval = setInterval(() => {
      setNotifications(prev => Math.max(0, prev + Math.floor(Math.random() * 3) - 1));
    }, 30000);

    return () => clearInterval(notificationInterval);
  }, []);

  useEffect(() => {
    // Listen for payment status updates
    const handlePaymentStatus = (event) => {
      setPaymentStatus(event.detail);
      setTimeout(() => setPaymentStatus(null), 5000);
    };

    window.addEventListener('paymentStatusUpdate', handlePaymentStatus);
    return () => window.removeEventListener('paymentStatusUpdate', handlePaymentStatus);
  }, []);

  const navigationItems = [
    {
      label: 'Dashboard',
      path: '/user-dashboard',
      icon: 'LayoutDashboard',
      roles: ['user', 'agent', 'admin']
    },
    {
      label: 'Bookings',
      path: '/payment-processing',
      icon: 'CreditCard',
      roles: ['user', 'agent', 'admin']
    },
    {
      label: 'Support',
      path: '/real-time-customer-support',
      icon: 'MessageCircle',
      roles: ['user', 'agent', 'admin'],
      hasNotification: notifications > 0,
      notificationCount: notifications
    },
    {
      label: 'Admin',
      path: '/admin-package-management',
      icon: 'Settings',
      roles: ['admin']
    },
    {
      label: 'Analytics',
      path: '/social-media-analytics-dashboard',
      icon: 'BarChart3',
      roles: ['admin', 'agent']
    }
  ];

  const filteredNavItems = navigationItems.filter(item => 
    user && item.roles.includes(user.role)
  );

  const handleNavigation = (path) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setUser(null);
    navigate('/login-registration');
  };

  const isActive = (path) => location.pathname === path;

  if (!user) {
    return (
      <header className="fixed top-0 left-0 right-0 bg-card border-b border-border z-1000">
        <div className="flex items-center justify-between h-16 px-4">
          <div className="flex items-center">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Plane" size={20} color="white" />
              </div>
              <span className="text-xl font-heading font-semibold text-foreground">
                Dru Travels
              </span>
            </div>
          </div>
          <Button 
            variant="default" 
            onClick={() => navigate('/login-registration')}
            iconName="LogIn"
            iconPosition="left"
          >
            Sign In
          </Button>
        </div>
      </header>
    );
  }

  return (
    <>
      {paymentStatus && (
        <div className={`fixed top-0 left-0 right-0 z-1005 px-4 py-3 text-center text-sm font-medium ${
          paymentStatus.type === 'success' ? 'bg-success text-success-foreground' :
          paymentStatus.type === 'error' ? 'bg-error text-error-foreground' :
          'bg-warning text-warning-foreground'
        }`}>
          <div className="flex items-center justify-center space-x-2">
            <Icon 
              name={paymentStatus.type === 'success' ? 'CheckCircle' : 
                    paymentStatus.type === 'error' ? 'XCircle' : 'Clock'} 
              size={16} 
            />
            <span>{paymentStatus.message}</span>
          </div>
        </div>
      )}
      
      <header className={`fixed top-0 left-0 right-0 bg-card border-b border-border z-1000 ${
        paymentStatus ? 'mt-12' : ''
      }`}>
        <div className="flex items-center justify-between h-16 px-4">
          <div className="flex items-center">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Plane" size={20} color="white" />
              </div>
              <span className="text-xl font-heading font-semibold text-foreground">
                Dru Travels
              </span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {filteredNavItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`relative flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-micro ${
                  isActive(item.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={item.icon} size={16} />
                <span>{item.label}</span>
                {item.hasNotification && (
                  <span className="absolute -top-1 -right-1 bg-error text-error-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {item.notificationCount > 9 ? '9+' : item.notificationCount}
                  </span>
                )}
              </button>
            ))}
          </nav>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            <div className="hidden md:flex items-center space-x-3">
              <span className="text-sm font-medium text-foreground">{user.name}</span>
              <span className="text-xs font-caption text-muted-foreground capitalize">
                {user.role}
              </span>
            </div>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="hidden md:flex"
            >
              <Icon name="LogOut" size={16} />
            </Button>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden"
            >
              <Icon name={isMenuOpen ? "X" : "Menu"} size={20} />
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-border bg-card">
            <div className="px-4 py-2 border-b border-border">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                  <Icon name="User" size={16} />
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground">{user.name}</div>
                  <div className="text-xs font-caption text-muted-foreground capitalize">
                    {user.role}
                  </div>
                </div>
              </div>
            </div>
            
            <nav className="py-2">
              {filteredNavItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  className={`relative w-full flex items-center space-x-3 px-4 py-3 text-left transition-micro ${
                    isActive(item.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item.icon} size={20} />
                  <span className="font-medium">{item.label}</span>
                  {item.hasNotification && (
                    <span className="ml-auto bg-error text-error-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {item.notificationCount > 9 ? '9+' : item.notificationCount}
                    </span>
                  )}
                </button>
              ))}
              
              <div className="border-t border-border mt-2 pt-2">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-left text-foreground hover:bg-muted transition-micro"
                >
                  <Icon name="LogOut" size={20} />
                  <span className="font-medium">Sign Out</span>
                </button>
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;